'use client';

import { useCallback, useEffect, useState } from 'react';

import { getHighlightedRaffleDetailsById } from '@/actions/highlightedRaffleDetails';
import type { HighlightedRaffleDetails } from '@/types/models/HighlightedRaffleDetails';

type HookResult = {
  data: HighlightedRaffleDetails | null;
  isLoading: boolean;
  error: string | null;
  refetch: () => Promise<void>;
};

export const useHighlightedRaffleDetails = (raffleId?: string): HookResult => {
  const [data, setData] = useState<HighlightedRaffleDetails | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchDetails = useCallback(async (id?: string) => {
    if (!id || id.trim() === '') {
      setData(null);
      setError('raffleId não fornecido');
      setIsLoading(false);
      return;
    }

    setIsLoading(true);
    setError(null);

    try {
      const response = await getHighlightedRaffleDetailsById(id, {
        caller: 'useHighlightedRaffleDetails',
      });
      setData(response ?? null);
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
      setData(null);
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    if (raffleId && raffleId.trim() !== '') {
      fetchDetails(raffleId);
    } else {
      setIsLoading(false);
      setError(null);
      setData(null);
    }
  }, [raffleId, fetchDetails]);

  const refetch = useCallback(async () => {
    await fetchDetails(raffleId);
  }, [fetchDetails, raffleId]);

  return { data, isLoading, error, refetch };
};
